// Self-contained survey chrome styles. Injected into a shadow root so
// consumer page CSS cannot leak in (or out). Neutral palette + clean
// sans-serif — no design-system dependency.
//
// CPTO can theme later by swapping these styles or exposing a theme prop.

export const SURVEY_STYLES = /* css */ `
  :host {
    --am-bg: #ffffff;
    --am-fg: #1a1a1a;
    --am-muted: #6b7280;
    --am-border: #e5e7eb;
    --am-accent: #2563eb;
    --am-accent-fg: #ffffff;
    --am-shadow: 0 12px 32px rgba(0, 0, 0, 0.16), 0 4px 8px rgba(0, 0, 0, 0.06);
    --am-radius: 12px;
    --am-spacing: 16px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    color: var(--am-fg);
    line-height: 1.4;
    font-size: 14px;
  }

  /* container — positioned via inline style by SurveyRenderer */
  .am-popup {
    background: var(--am-bg);
    border-radius: var(--am-radius);
    box-shadow: var(--am-shadow);
    padding: 20px 20px 16px;
    width: 360px;
    max-width: calc(100vw - 32px);
    box-sizing: border-box;
    border: 1px solid var(--am-border);
    animation: am-fade-in 180ms ease-out;
  }

  @keyframes am-fade-in {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .am-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .am-title {
    font-size: 15px;
    font-weight: 600;
    margin: 0;
  }

  .am-close {
    appearance: none;
    background: transparent;
    border: none;
    color: var(--am-muted);
    font-size: 18px;
    line-height: 1;
    padding: 4px;
    cursor: pointer;
    margin: -4px -4px 0 0;
    border-radius: 6px;
  }
  .am-close:hover { color: var(--am-fg); background: var(--am-border); }

  .am-q {
    margin-bottom: var(--am-spacing);
  }
  .am-q:last-of-type { margin-bottom: 8px; }

  .am-text {
    margin: 0 0 10px;
    font-size: 14px;
  }

  /* scale buttons (NPS / CSAT / CES) */
  .am-scale {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  .am-scale-btn {
    appearance: none;
    flex: 1 1 auto;
    min-width: 28px;
    padding: 8px 0;
    border: 1px solid var(--am-border);
    background: var(--am-bg);
    color: var(--am-fg);
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    transition: background 80ms, border-color 80ms;
  }
  .am-scale-btn:hover { background: var(--am-border); }
  .am-scale-btn.am-selected {
    background: var(--am-accent);
    border-color: var(--am-accent);
    color: var(--am-accent-fg);
  }

  /* star buttons */
  .am-stars { display: flex; gap: 4px; }
  .am-star-btn {
    appearance: none;
    border: none;
    background: transparent;
    color: var(--am-border);
    font-size: 28px;
    line-height: 1;
    cursor: pointer;
    padding: 4px;
  }
  .am-star-btn:hover,
  .am-star-btn.am-selected,
  .am-star-btn.am-hover-active { color: #f59e0b; }

  /* multiple choice */
  .am-mc-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .am-mc-option {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 8px 10px;
    border: 1px solid var(--am-border);
    border-radius: 8px;
  }
  .am-mc-option:hover { background: rgba(37, 99, 235, 0.04); }
  .am-mc-option input { accent-color: var(--am-accent); }

  /* open text */
  .am-textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 8px 10px;
    border: 1px solid var(--am-border);
    border-radius: 8px;
    font: inherit;
    resize: vertical;
    min-height: 80px;
  }
  .am-textarea:focus {
    outline: none;
    border-color: var(--am-accent);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
  }

  /* link CTA */
  .am-cta-link {
    display: inline-block;
    padding: 9px 16px;
    background: var(--am-accent);
    color: var(--am-accent-fg);
    border-radius: 8px;
    text-decoration: none;
    font-weight: 500;
    font-size: 14px;
  }
  .am-cta-link:hover { filter: brightness(1.05); }

  /* footer */
  .am-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
  }
  .am-btn-submit {
    appearance: none;
    padding: 9px 16px;
    background: var(--am-accent);
    color: var(--am-accent-fg);
    border: none;
    border-radius: 8px;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
  }
  .am-btn-submit:hover { filter: brightness(1.05); }
  .am-btn-submit:disabled { opacity: 0.55; cursor: not-allowed; }

  /* Position helpers — applied to the host element by SurveyRenderer.
     Defined here so they're always part of the same stylesheet. */
  :host(.am-position-bottom-right) {
    position: fixed; bottom: 24px; right: 24px; z-index: 2147483647;
  }
  :host(.am-position-bottom-center) {
    position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); z-index: 2147483647;
  }
  :host(.am-position-center) {
    position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 2147483647;
  }
  :host(.am-position-top-center) {
    position: fixed; top: 24px; left: 50%; transform: translateX(-50%); z-index: 2147483647;
  }
`;
